import type { ReactNode } from "react";
import type { DriveActivity } from "@/api/driveApi";
import { formatBytes, formatDateTime, formatRelative, plural } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ItemIcon } from "../items/ItemIcon";

/** "anna.kumar@x.com" → "Anna Kumar"; the signed-in user → "You"; the bin's auto-purge → "Drive". */
export function personName(email: string, me: string | undefined): string {
    if (email === "system") return "Drive";
    if (email === "file-request") return "File request";
    if (me && email.toLowerCase() === me.toLowerCase()) return "You";
    const local = email.split("@")[0] ?? email;
    const words = local.split(/[._-]+/).filter(Boolean);
    if (words.length === 0) return email;
    return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/** "you, Anna Kumar and Ravi" from the detail's comma-separated emails. */
function people(csv: string | undefined, me: string | undefined): string {
    const names = (csv ?? "")
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean)
        .map((e) => {
            const n = personName(e, me);
            return n === "You" ? "you" : n;
        });
    if (names.length === 0) return "someone";
    if (names.length === 1) return names[0]!;
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function Em({ children }: { children: ReactNode }) {
    return <strong className="break-words font-medium text-foreground">{children}</strong>;
}

const AUDIENCE: Record<string, string> = {
    anonymous: "anyone with the link",
    domain: "people in the organisation",
    email: "specific people"
};

/** The sentence for one entry, e.g. "Anna renamed Report.docx to Q3 Report.docx". */
export function activitySentence(a: DriveActivity, me: string | undefined): ReactNode {
    const d = a.detail ?? {};
    const actor = <span className="font-medium text-foreground">{personName(a.actorEmail, me)}</span>;
    const item = <Em>{a.itemName}</Em>;
    const role = d.role === "editor" ? "an editor" : "a viewer";
    const count = Number(d.count ?? "0");
    switch (a.action) {
        case "created":
            return <>{actor} created {item}</>;
        case "uploaded":
            return <>{actor} uploaded {item}</>;
        case "version":
            return <>{actor} saved a new version of {item}{d.version ? ` (version ${d.version})` : ""}</>;
        case "version-restored":
            return <>{actor} restored {item} to version {d.version ?? "?"}</>;
        case "versions-pruned":
            return (
                <>
                    {actor} deleted {plural(count, "old version")} of {item}
                    {d.bytes ? `, freeing ${formatBytes(Number(d.bytes))}` : ""}
                </>
            );
        case "renamed":
            return <>{actor} renamed <Em>{d.from ?? "an item"}</Em> to <Em>{d.to ?? a.itemName}</Em></>;
        case "moved":
            return <>{actor} moved {item} from <Em>{d.from ?? "a folder"}</Em> to <Em>{d.to ?? "a folder"}</Em></>;
        case "copied":
            return d.source && d.source !== a.itemName
                ? <>{actor} made a copy of <Em>{d.source}</Em> as {item}</>
                : <>{actor} made a copy of {item}</>;
        case "zipped":
            return <>{actor} created the archive {item}{count > 0 ? ` from ${plural(count, "item")}` : ""}</>;
        case "trashed":
            return <>{actor} moved {item} to the bin</>;
        case "restored":
            return <>{actor} restored {item} from the bin</>;
        case "deleted":
            return d.via === "auto-purge"
                ? <>{item} was deleted forever after 30 days in the bin</>
                : <>{actor} deleted {item} forever</>;
        case "shared":
            return (
                <>
                    {actor} shared {item} with {people(d.emails, me)} as {role}
                    {d.allowDownload === "false" ? " (download blocked)" : ""}
                </>
            );
        case "access-changed":
            return (
                <>
                    {actor} made {people(d.emails, me)} {role} of {item}
                    {d.allowDownload === "false" ? " (download blocked)" : ""}
                </>
            );
        case "access-removed":
            return d.self === "true"
                ? <>{actor} left {item}</>
                : <>{actor} removed {people(d.emails, me)} from {item}</>;
        case "link-created":
            return (
                <>
                    {actor} created a link to {item} for {AUDIENCE[d.audience ?? ""] ?? "anyone with the link"}
                    {d.permission === "edit" ? " (can edit)" : ""}
                </>
            );
        case "link-deleted":
            return <>{actor} deleted a link to {item}</>;
        case "unzipped":
            return <>{actor} extracted {item}{d.folder ? <> into <Em>{d.folder}</Em></> : null}</>;
        case "protected":
            return d.source
                ? <>{actor} created the protected PDF {item} from <Em>{d.source}</Em></>
                : <>{actor} password-protected {item}</>;
        case "commented":
            return <>{actor} commented on {item}<CommentLine text={d.comment} /></>;
        case "comment-replied":
            return <>{actor} replied to a comment on {item}<CommentLine text={d.comment} /></>;
        case "comment-resolved":
            return <>{actor} resolved a comment on {item}<CommentLine text={d.comment} /></>;
        case "comment-reopened":
            return <>{actor} reopened a comment on {item}<CommentLine text={d.comment} /></>;
        case "request-uploaded":
            // Name + email are typed by an anonymous uploader — shown as given, never as a Drive user.
            return (
                <>
                    <Em>{d.name ?? "Someone"}</Em>{d.email ? ` (${d.email})` : ""} uploaded {item}
                    {d.request ? <> through the file request <Em>{d.request}</Em></> : " through a file request"}
                </>
            );
        case "file-request-created":
            return <>{actor} created the file request <Em>{d.request ?? "Untitled"}</Em> for {item}</>;
        case "file-request-closed":
            return <>{actor} closed the file request <Em>{d.request ?? "Untitled"}</Em> on {item}</>;
        case "file-request-reopened":
            return <>{actor} reopened the file request <Em>{d.request ?? "Untitled"}</Em> on {item}</>;
        case "file-request-deleted":
            return <>{actor} deleted the file request <Em>{d.request ?? "Untitled"}</Em> on {item}</>;
        default:
            return <>{actor} changed {item}</>;
    }
}

/** The comment itself under the sentence — plain text, one line. */
function CommentLine({ text }: { text: string | undefined }) {
    return text ? <span className="mt-0.5 block truncate text-muted-foreground">“{text}”</span> : null;
}

const AVATAR_TONES = [
    "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300",
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
    "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
    "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300",
    "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300",
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-950/60 dark:text-cyan-300"
];

function toneFor(email: string): string {
    let h = 0;
    for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) | 0;
    return AVATAR_TONES[Math.abs(h) % AVATAR_TONES.length]!;
}

export function ActorAvatar({ email, me }: { email: string; me: string | undefined }) {
    const name = personName(email, me);
    const initial = name === "You" ? (me ?? "Y").charAt(0).toUpperCase() : name.charAt(0).toUpperCase();
    return (
        <span
            aria-hidden
            className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold", toneFor(email.toLowerCase()))}
        >
            {initial}
        </span>
    );
}

/**
 * One timeline row: avatar, sentence, item icon + relative time. Clickable
 * (opens the item via /open/<id>) unless the item is gone — then it says so.
 */
export function ActivityRow({
    activity: a,
    me,
    onOpen
}: {
    activity: DriveActivity;
    me: string | undefined;
    /** Omit to render a plain row (e.g. the item's own timeline). */
    onOpen?: (a: DriveActivity) => void;
}) {
    const body = (
        <>
            <ActorAvatar email={a.actorEmail} me={me} />
            <span className="min-w-0 flex-1">
                <span className="block text-sm leading-snug text-foreground/85">{activitySentence(a, me)}</span>
                <span className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
                    <ItemIcon item={{ kind: a.itemKind, name: a.itemName, contentType: a.contentType ?? null }} className="h-3.5 w-3.5" />
                    <time dateTime={a.createdAt} title={formatDateTime(a.createdAt)}>{formatRelative(a.createdAt)}</time>
                    {a.itemState === "deleted" ? (
                        <span className="rounded bg-muted px-1.5 py-px text-[10px] font-medium uppercase tracking-wide">Deleted</span>
                    ) : a.itemState === "trashed" ? (
                        <span className="rounded bg-amber-100 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">In bin</span>
                    ) : null}
                </span>
            </span>
        </>
    );
    if (onOpen && a.itemState !== "deleted") {
        return (
            <button
                type="button"
                onClick={() => onOpen(a)}
                className="flex w-full gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                {body}
            </button>
        );
    }
    return <div className="flex gap-3 px-2 py-2.5">{body}</div>;
}
